/** @jsx jsx */
import { Box, Container } from "@eworkit/iris";
import { FC, useCallback, useContext, useMemo } from "react";
import { jsx } from "theme-ui";

import { AppSuspense } from "@/components/app";
import { useFeatureFlags } from "@/components/FeatureFlags";
import { UserContext } from "@/components/User";
import { FEATURE_FLAG } from "@/data/feature-flags";
import { JobApplication } from "@/data/job-applications/types/JobApplication";
import { RoutedTabPanels } from "@/router/RoutedTabPanels";
import { JobApplicationDetailsDetailsTab } from "@/views/job-applications/details/_components/JobApplicationDetailsDetailsTab";
import { JobApplicationDetailsLifecycleTab } from "@/views/job-applications/details/_components/JobApplicationDetailsLifecycleTab";
import { JobApplicationDetailsMessagesTab } from "@/views/job-applications/details/_components/JobApplicationDetailsMessagesTab";
import { JobApplicationDetailsRelatedTab } from "@/views/job-applications/details/_components/JobApplicationDetailsRelatedTab";

interface JobApplicationDetailsTabPanelsProps {
  jobApplication: JobApplication;
}

export const JobApplicationDetailsTabPanels: FC<JobApplicationDetailsTabPanelsProps> = ({ jobApplication }) => {
  const { contextCompany, hasCompanyManagerPrivileges } = useContext(UserContext);

  const { isFeatureEnabled } = useFeatureFlags();

  const details = useCallback(
    () => <JobApplicationDetailsDetailsTab jobApplication={jobApplication} />,
    [jobApplication],
  );

  const lifecycle = useCallback(
    () => <JobApplicationDetailsLifecycleTab jobApplication={jobApplication} />,
    [jobApplication],
  );

  const related = useCallback(
    () => <JobApplicationDetailsRelatedTab jobApplication={jobApplication} />,
    [jobApplication],
  );

  const messages = useCallback(
    () => <JobApplicationDetailsMessagesTab jobApplication={jobApplication} />,
    [jobApplication],
  );

  const tabPanels = useMemo(
    () => ({
      details,
      lifecycle,
      related,
      messages:
        isFeatureEnabled(FEATURE_FLAG.MESSAGE_CENTER) && (!contextCompany || hasCompanyManagerPrivileges())
          ? messages
          : undefined,
    }),
    [details, lifecycle, related, messages, isFeatureEnabled, contextCompany, hasCompanyManagerPrivileges],
  );

  return (
    <Container>
      <Box px={[null, null, null, "sm"]} py={["xs", null, null, "md"]}>
        <AppSuspense>
          <RoutedTabPanels routeParam="tab" views={tabPanels} />
        </AppSuspense>
      </Box>
    </Container>
  );
};
